import { BaseMessage } from '~/utils/baseMessage'
import { defineStore } from 'pinia'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import {
  createPrefixedPersistStorage,
  getAuthCookieOptions,
  resolveStoragePrefix,
} from '~/utils/storageKey'
import { normalizeChinaMobile } from '~/utils/phone'
import { normalizeAccountFundsPayload } from '~/utils/store/accountFunds'
import {
  IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS,
  hasTokenRefreshProgressed,
  resolveAccessTokenExpiresAt,
  resolveNoProgressRetryAt,
  resolveTokenRefreshDelay,
} from '~/utils/imsTokenTiming'
import {
  canPlaceStoreOrderFromUserInfo,
  hasStoreAgencyIdentity,
  isCertifiedEngineerUser,
  isStoreAgencyUser,
  shouldBlockStoreOrderFromUserInfo,
} from '~/utils/store/userIdentity'

export { hasStoreAgencyIdentity, isStoreAgencyUser } from '~/utils/store/userIdentity'

/** @description 已登录非代理商尝试下单时的统一提示文案 */
export const NON_AGENCY_ORDER_NOTICE = '当前客户身份暂不支持线上下单'
const USER_INFO_CACHE_TTL = 30 * 1000
const IMS_USER_INFO_CACHE_TTL = 30 * 1000
const IMS_ACCESS_TOKEN_REFRESH_LOCK = 'tenda-ims-access-token-refresh'

const isRecentFetch = (fetchedAt, ttl) => {
  return Boolean(fetchedAt && Date.now() - fetchedAt < ttl)
}

const resolveFetchOptions = (options) => {
  if (typeof options === 'boolean') return { force: options }
  return options || {}
}

/**
 * @description 移动端非代理商点击加购/下单时的提示
 */
export function notifyNonAgencyOrderBlocked() {
  if (!import.meta.client) return
  BaseMessage.warning({
    content: NON_AGENCY_ORDER_NOTICE,
    class: 'agency-order-blocked-message',
  })
}

/**
 * @description 统一清洗用户信息里的手机号字段，避免国际区号格式污染前端展示和浏览器密码回填。
 * @param {Object | null | undefined} user - 原始用户信息
 * @returns {Object | null | undefined} 清洗后的用户信息
 */
const normalizeUserPhoneFields = (user) => {
  if (!user || typeof user !== 'object') {
    return user
  }

  const normalizedUser = { ...user }
  ;['account_name', 'phone', 'mobile'].forEach((field) => {
    if (normalizedUser[field] !== undefined && normalizedUser[field] !== null) {
      normalizedUser[field] = normalizeChinaMobile(normalizedUser[field])
    }
  })

  return normalizedUser
}

/**
 * @description 商城 userInfo 与钱包同级的额度字段别名：kyed→可用、klimk→总额；仅在本次 setUserInfo 入参携带对应键时写入规范字段，避免误用历史残留。
 * @param {Record<string, unknown>} patch - 已通过 null/undefined 过滤后的增量字段
 * @returns {void}
 */
const applyQuotaAliasesFromPatch = (patch) => {
  if (!patch || typeof patch !== 'object') return
  if (Object.prototype.hasOwnProperty.call(patch, 'kyed')) {
    const n = Number(patch.kyed)
    if (Number.isFinite(n)) {
      patch.manufacturerQuotaRemain = n
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'klimk')) {
    const n = Number(patch.klimk)
    if (Number.isFinite(n)) {
      patch.manufacturerQuotaTotal = n
    }
  }
}

const ACCOUNT_PROFILE_DISPLAY_FIELDS = [
  'nick_name',
  'nick',
  'nickname',
  'legalPersonName',
  'head_icon',
  'icon',
  'avatar',
  'account_name',
  'phone',
  'mobile',
]

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

/**
 * @description 是否已有 IMS 个人资料字段。用于刷新页面后复用持久化资料，避免 TDC 刷新覆盖个人中心展示。
 * @param {Record<string, unknown>|null|undefined} userInfo
 * @returns {boolean}
 */
const hasImsProfileInfo = (userInfo) => {
  if (!userInfo || typeof userInfo !== 'object') return false
  return ['cloud_code', 'source'].some((key) => {
    const value = userInfo[key]
    return value !== undefined && value !== null && value !== ''
  })
}

/**
 * @description IMS 已同步后，TDC 刷新只更新业务字段，不覆盖头像/昵称/手机号等个人中心展示字段。
 * @param {Record<string, unknown>} userInfo
 * @returns {Record<string, unknown>}
 */
const omitAccountProfileDisplayFields = (userInfo) => {
  const nextUserInfo = { ...userInfo }
  ACCOUNT_PROFILE_DISPLAY_FIELDS.forEach((field) => {
    if (hasOwn(nextUserInfo, field)) {
      delete nextUserInfo[field]
    }
  })
  return nextUserInfo
}

/**
 * @typedef {Object} UserInfo
 * @property {string} [userId] - 用户ID
 * @property {string} [account_name] - 账号名/手机号
 * @property {string} [nick_name] - 昵称
 * @property {string} [head_icon] - 头像URL
 * @property {number} [userType] - 用户类型：0代表SMB，1代表企业工程商，2代表个体工程商已认证，3个体工程商未认证
 * @property {number} [agency] - 是否代理商（GET /store/user/info）：0 否，1 是
 * @property {boolean} [allChannelIdentitiesDisabled] - 当前销售组织的渠道身份是否全部停用，仅供账号中心展示
 * @property {number} [agencyLevel] - 代理商等级（仅 agency=1 时有值）：1 黄金 / 2 钻石
 * @property {number} [engineerStatus] - 工程商审核状态：-1待认证, 0待审核, 1审核通过, 2审核拒绝
 * @property {number} [engineerVerify] - 工程商验证弹窗状态（null或-1：不显示，1：显示）
 * @property {number} [accountStatus] - 账户状态：1正常 2已冻结 3已注销
 * @property {number} [balance] - 可提现余额
 * @property {number|string} [cashAvailableBalance] - 现款客户可用余额（个人中心接口 balance.available）
 * @property {number} [settleAmount] - 待结算金额
 * @property {number} [manufacturerQuotaRemain] - 厂家剩余可用额度（规范字段；接口同层别名 kyed）
 * @property {number} [manufacturerQuotaTotal] - 厂家总额度（规范字段；接口同层别名 klimk）
 * @property {number} [kyed] - 代理商可用额度（/store/user/info 与 balance 同级，写入 store 时映射为 manufacturerQuotaRemain）
 * @property {number} [klimk] - 代理商总额度（与 balance 同级，映射为 manufacturerQuotaTotal）
 * @property {number} [couponNum] - 优惠券数量
 * @property {number} [solutionNum] - 配单方案数量
 * @property {number} [collectionNum] - 收藏数量
 * @property {number} [unPay] - 待付款订单数
 * @property {number} [pendingAudit] - 代理商待审核订单数（与订单中心 orderCountData.pendingAudit 对齐）
 * @property {number} [payed] - 待发货订单数
 * @property {number} [consignment] - 待收货订单数
 * @property {number} [confirm] - 待评价订单数
 * @property {number} [refund] - 退款/售后订单数
 * @property {string} [create_time] - 注册时间
 */

/**
 * @description 用户状态管理 Store
 * @description 包含用户信息、登录状态、token 管理等
 */
// TODO 2026-03-15之后需要修改为false
export const IMS_DEV_MODE = false // 全局开发模式开关：3-15前所有环境写死 Token

export const useUserStore = defineStore('user', {
  state: () => ({
    /** @type {UserInfo | null} */
    userInfo: null,
    isLoggedIn: false,
    // IMS token 相关（成都团队）
    accessToken: null, // access_token
    refreshToken: null, // refresh_token
    tokenExpiresAt: null, // access_token 过期时间戳（毫秒）
    refreshTokenExpiresAt: null, // refresh_token 过期时间戳（毫秒），从登录回调开始3天
    tokenRefreshTimer: null, // token 刷新定时器 ID
    tokenRefreshNotBefore: 0, // 服务端未推进 token 时，补偿刷新不得早于此时间
    tokenRefreshNoProgressCount: 0, // 连续刷新成功但 token/过期时间未推进次数
    userInfoPromise: null, // 用户信息请求 Promise，防止重复请求
    imsUserInfoPromise: null, // IMS 用户信息请求 Promise，防止重复请求
    manufacturerQuotaPromise: null, // 厂家授信额度请求 Promise，防止重复请求
    manufacturerQuotaRequestVersion: 0, // 资金请求代次，阻止退出/切换账号后的旧响应回写
    manufacturerQuotaLoaded: false, // 厂家授信额度是否已完成本轮加载，避免 klimk 默认值导致客户类型闪烁
    userInfoFetchedAt: 0, // 最近一次 TDC 用户信息成功请求时间
    imsUserInfoFetchedAt: 0, // 最近一次 IMS 用户信息成功请求时间
    hasSyncedImsProfileInfo: false, // IMS 个人资料是否已接管头像/昵称/手机号展示
    refreshPromise: null, // token 刷新请求 Promise，防止重复请求
  }),
  persist: {
    key: 'user',
    pick: [
      'userInfo',
      'isLoggedIn',
      'accessToken',
      'refreshToken',
      'tokenExpiresAt',
      'refreshTokenExpiresAt',
      'tokenRefreshNotBefore',
      'tokenRefreshNoProgressCount',
    ],
    storage: createPrefixedPersistStorage(() => {
      try {
        const config = useRuntimeConfig()
        return resolveStoragePrefix(config.public)
      } catch {
        return resolveStoragePrefix()
      }
    }),
  },
  getters: {
    /**
     * @description 检查 access_token 是否进入 IMS 约定的到期前 60 秒刷新窗口
     * @returns {boolean}
     */
    isTokenExpiringSoon() {
      if (!this.tokenExpiresAt) return false
      const now = Date.now()
      if (this.tokenRefreshNotBefore && now < this.tokenRefreshNotBefore) return false
      return now >= this.tokenExpiresAt - IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS * 1000
    },
    /**
     * @description 检查 access_token 是否已过期
     * @returns {boolean}
     */
    isTokenExpired() {
      if (!this.tokenExpiresAt) return true
      return Date.now() >= this.tokenExpiresAt
    },
    /**
     * @description 是否允许立即刷新 access_token，统一供定时器、路由和请求拦截器使用。
     * @returns {boolean}
     */
    shouldRefreshAccessToken() {
      const now = Date.now()
      if (this.tokenRefreshNotBefore && now < this.tokenRefreshNotBefore) return false
      if (!this.tokenExpiresAt) return true
      return now >= this.tokenExpiresAt - IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS * 1000
    },
    /**
     * @description 检查 refresh_token 是否已过期
     * @returns {boolean}
     */
    isRefreshTokenExpired() {
      if (!this.refreshTokenExpiresAt) return false // 如果没有设置过期时间，认为未过期（兼容旧数据）
      return Date.now() >= this.refreshTokenExpiresAt
    },
    /**
     * @description 是否为代理商（/store/user/info 的 agency === 1）
     * @returns {boolean}
     */
    isAgencyUser() {
      return isStoreAgencyUser(this.userInfo)
    },
    /**
     * @description 代理商等级（userInfo.agencyLevel 数值化，供黄金/钻石等展示）
     * @returns {number|undefined}
     */
    agencyLevel() {
      if (
        !this.userInfo ||
        this.userInfo.agencyLevel === undefined ||
        this.userInfo.agencyLevel === null
      ) {
        return undefined
      }
      const n = Number(this.userInfo.agencyLevel)
      return Number.isFinite(n) ? n : undefined
    },
    /**
     * @description 是否已从 /store/user/info 解析出 agency 身份
     * @returns {boolean}
     */
    hasAgencyIdentity() {
      return hasStoreAgencyIdentity(this.userInfo)
    },
    /**
     * @description 是否允许商城下单（已登录且 /store/user/info agency === 1）
     * @returns {boolean}
     */
    canPlaceStoreOrder() {
      if (!this.isLoggedIn) return false
      // F81：代理商恒允许；工程商/终端客户看服务端 orderPlacementEnabled
      return canPlaceStoreOrderFromUserInfo(this.userInfo)
    },
    /**
     * @description 非代理商是否应拦截下单（已登录且身份已加载且开关未开）
     * @returns {boolean}
     */
    shouldBlockStoreOrder() {
      if (!this.isLoggedIn) return false
      // F81：身份未加载不弹提示，避免登录过程误拦
      return shouldBlockStoreOrderFromUserInfo(this.userInfo)
    },
    /**
     * @description 商品卡是否隐藏加购（未登录或非代理商均隐藏）
     * @returns {boolean}
     */
    shouldHideProductCardCart() {
      return !this.canPlaceStoreOrder
    },
    /**
     * @description 获取用户角色
     * @description 根据 userType 判断：0=SMB, 1或2=PRO, 3=SMB（未认证个体工程商）
     * @returns {string} 'smb' | 'pro'
     */
    role() {
      if (
        !this.userInfo ||
        this.userInfo.userType === undefined ||
        this.userInfo.userType === null
      ) {
        return 'smb' // 默认 SMB
      }

      // userType = 1（企业工程商）或 2（个体工程商已认证）→ pro
      if (isCertifiedEngineerUser(this.userInfo)) {
        return 'pro'
      }

      // userType = 0（SMB）或 3（个体工程商未认证）→ smb
      return 'smb'
    },
  },
  actions: {
    /**
     * @description 确保已拉取 /store/user/info（含 agency），供下单拦截等场景使用
     * @returns {Promise<Object|null|undefined>}
     */
    async ensureStoreAgencyIdentity() {
      if (!import.meta.client || !this.isLoggedIn) return null
      if (hasStoreAgencyIdentity(this.userInfo)) return this.userInfo
      try {
        return await this.fetchUserInfo()
      } catch (error) {
        console.error('[UserStore] Failed to ensure store agency identity:', error)
        return null
      }
    },

    /**
     * @description 非代理商下单拦截：全端弹提示，返回 true 表示应中止后续流程
     * @returns {boolean}
     */
    guardStoreOrderOrNotify() {
      if (!this.shouldBlockStoreOrder) return false
      notifyNonAgencyOrderBlocked()
      return true
    },

    /**
     * @description 同步 access_token 到 Cookie（用于 SSR 刷新时携带 token）
     * @param {string|null} token - access_token
     * @returns {void}
     */
    syncAuthTokenCookie(token) {
      if (!import.meta.client) return
      const keys = getRuntimeAuthKeys()
      const tokenCookie = useCookie(keys.accessTokenCookie, getAuthCookieOptions())
      tokenCookie.value = token || ''
    },

    /**
     * @description 同步 refresh_token 到 Cookie（用于 SSR 携带，成都 IMS 接口需要）
     * @param {string|null} token - refresh_token
     * @returns {void}
     */
    syncRefreshTokenCookie(token) {
      if (!import.meta.client) return
      const keys = getRuntimeAuthKeys()
      const refreshTokenCookie = useCookie(keys.refreshTokenCookie, getAuthCookieOptions())
      refreshTokenCookie.value = token || ''
    },

    /**
     * @description 从 Cookie 恢复 IMS token 到 Pinia，避免页面刷新后 store 未恢复导致无法自动刷新 token
     * @reason API 拦截器和登录回调都依赖 store.refreshToken，cookie 中存在 refresh_token 时必须优先恢复
     * @returns {boolean} 是否恢复到任意 token
     */
    restoreImsTokenFromCookies() {
      if (!import.meta.client) return false

      const keys = getRuntimeAuthKeys()
      const accessTokenCookie = useCookie(keys.accessTokenCookie, { readonly: true })
      const refreshTokenCookie = useCookie(keys.refreshTokenCookie, { readonly: true })
      const accessToken = accessTokenCookie.value || ''
      const refreshToken = refreshTokenCookie.value || ''

      if (!accessToken && !refreshToken) return false

      this.$patch({
        accessToken: this.accessToken || accessToken || null,
        refreshToken: this.refreshToken || refreshToken || null,
        isLoggedIn: true,
      })
      this.syncLoginCookie(true)

      return true
    },

    /**
     * @description 设置用户信息
     * @param {Object} user 用户对象
     * @returns {void}
     */
    setUserInfo(user) {
      // 如果传入 null，说明是清空用户信息
      if (!user) {
        this.manufacturerQuotaRequestVersion += 1
        this.manufacturerQuotaPromise = null
        this.$patch({
          userInfo: null,
          isLoggedIn: false,
          userInfoFetchedAt: 0,
          imsUserInfoFetchedAt: 0,
          hasSyncedImsProfileInfo: false,
          manufacturerQuotaLoaded: false,
        })
        this.syncLoginCookie(false)
        if (import.meta.client) {
          try {
            const nuxtApp = useNuxtApp()
            if (nuxtApp.$setMonitorUser) nuxtApp.$setMonitorUser(null, null)
          } catch (_) {}
        }
        return
      }

      // 过滤掉新数据中的 null 和 undefined，避免覆盖原有有效数据
      // @reason 深圳和成都接口字段有重叠，需确保重要信息（如 userType）不被覆盖
      const normalizedUser = normalizeUserPhoneFields(user)
      const cleanNewData = Object.fromEntries(
        Object.entries(normalizedUser).filter(([_, v]) => v !== undefined && v !== null)
      )

      applyQuotaAliasesFromPatch(cleanNewData)

      // 使用 $patch 确保触发持久化插件的订阅，并采用合并策略
      const updatedUserInfo = { ...(this.userInfo || {}), ...cleanNewData }
      const previousUserId = this.userInfo?.userId ?? this.userInfo?.id ?? null
      const nextUserId = updatedUserInfo.userId ?? updatedUserInfo.id ?? null
      if (previousUserId && nextUserId && previousUserId !== nextUserId) {
        this.manufacturerQuotaRequestVersion += 1
        this.manufacturerQuotaPromise = null
        this.manufacturerQuotaLoaded = false
      }
      this.$patch({
        userInfo: updatedUserInfo,
        isLoggedIn: !!Object.keys(updatedUserInfo).length,
      })
      this.syncLoginCookie(this.isLoggedIn)
      if (import.meta.client) {
        try {
          const nuxtApp = useNuxtApp()
          const userId = updatedUserInfo.userId ?? updatedUserInfo.id ?? null
          if (nuxtApp.$setMonitorUser) nuxtApp.$setMonitorUser(userId, updatedUserInfo)
        } catch (_) {}
      }

      // 强制落盘 localStorage，防止持久化插件在某些时序下失效
      if (import.meta.client && typeof localStorage !== 'undefined') {
        try {
          const keys = getRuntimeAuthKeys()
          const stored = localStorage.getItem(keys.userPersistKey)
          const current = stored ? JSON.parse(stored) : {}
          localStorage.setItem(
            keys.userPersistKey,
            JSON.stringify({
              ...current,
              userInfo: updatedUserInfo,
              isLoggedIn: !!updatedUserInfo,
            })
          )
        } catch (e) {
          console.error('[UserStore] 强制写入 localStorage 失败:', e)
        }
      }
    },
    /**
     * @description 同步登录状态到 Cookie（SSR 刷新保持登录态）
     * @param {boolean} isLoggedIn 是否已登录
     * @returns {void}
     */
    syncLoginCookie(isLoggedIn) {
      const keys = getRuntimeAuthKeys()
      const loginCookie = useCookie(keys.loginCookie, getAuthCookieOptions())
      loginCookie.value = isLoggedIn ? '1' : '0'
    },
    /**
     * @description 设置 IMS token
     * @param {string} accessToken - access_token
     * @param {string} refreshToken - refresh_token
     * @param {number} expiresIn - access_token 过期时间（小时）
     * @param {number} createTime - access_token 创建时间戳（秒）
     * @param {boolean} isNewRefreshToken - 是否是新的 refresh_token（登录回调时传入 true）
     */
    setImsToken(
      accessToken,
      refreshToken,
      expiresIn = 1,
      createTime = null,
      isNewRefreshToken = false
    ) {
      this.accessToken = accessToken
      this.refreshToken = refreshToken
      this.syncAuthTokenCookie(accessToken)
      this.syncRefreshTokenCookie(refreshToken)

      this.tokenExpiresAt = resolveAccessTokenExpiresAt({
        createTime,
        expiresInHours: expiresIn,
      })
      this.tokenRefreshNotBefore = 0
      this.tokenRefreshNoProgressCount = 0

      // 如果是新的 refresh_token（登录回调），设置 refresh_token 过期时间为3天后
      if (isNewRefreshToken) {
        const threeDaysInMs = 3 * 24 * 60 * 60 * 1000 // 3天
        this.refreshTokenExpiresAt = Date.now() + threeDaysInMs
      }
      // 如果不是新的 refresh_token，保持原有的 refreshTokenExpiresAt 不变

      // 启动定时刷新
      this.startTokenRefreshTimer()
    },
    /**
     * @description 清除 IMS token
     */
    clearImsToken() {
      this.accessToken = null
      this.refreshToken = null
      this.tokenExpiresAt = null
      this.refreshTokenExpiresAt = null
      this.tokenRefreshNotBefore = 0
      this.tokenRefreshNoProgressCount = 0
      this.stopTokenRefreshTimer()
      this.syncAuthTokenCookie(null)
      this.syncRefreshTokenCookie(null)
    },
    /**
     * @description 启动 token 刷新定时器（按 IMS refresh_threshold 提前 60 秒刷新）
     * @param {Object} [options] - 定时参数
     * @param {number} [options.notBefore] - 本次刷新不得早于的毫秒时间戳
     * @returns {void}
     */
    startTokenRefreshTimer({ notBefore = this.tokenRefreshNotBefore } = {}) {
      // 清除旧的定时器
      this.stopTokenRefreshTimer()

      if (!this.tokenExpiresAt || !this.refreshToken) return

      if (import.meta.client) {
        const refreshDelay = resolveTokenRefreshDelay({
          expiresAt: this.tokenExpiresAt,
          notBefore,
        })
        this.tokenRefreshTimer = window.setTimeout(() => {
          this.tokenRefreshTimer = null
          this.refreshAccessToken().catch(async (error) => {
            console.error('[UserStore] 定时刷新 access_token 失败:', error)
            if (this.isLoggedIn) {
              await this.logout(true)
            }
          })
        }, refreshDelay)
      }
    },
    /**
     * @description 停止 token 刷新定时器
     */
    stopTokenRefreshTimer() {
      if (this.tokenRefreshTimer && import.meta.client) {
        window.clearTimeout(this.tokenRefreshTimer)
        this.tokenRefreshTimer = null
      }
    },
    /**
     * @description 刷新 access_token
     * @param {Object} [options] - 刷新选项
     * @param {boolean} [options.force=false] - 是否忽略本地有效期并强制刷新
     * @param {boolean} [options.skipCrossTabLock=false] - 是否已持有跨页签刷新锁
     * @returns {Promise<Object>} 返回刷新结果，可能包含 cloud_code
     */
    async refreshAccessToken({ force = false, skipCrossTabLock = false } = {}) {
      // 如果已经有正在进行的刷新请求，直接返回该 Promise
      if (this.refreshPromise) {
        return this.refreshPromise
      }

      // 同一浏览器的多个页签共用一个刷新锁。后进入的页签优先复用已落盘的新 token。
      if (import.meta.client && !skipCrossTabLock && navigator.locks?.request) {
        const accessTokenBeforeLock = this.accessToken
        const expiresAtBeforeLock = Number(this.tokenExpiresAt) || 0

        return navigator.locks.request(IMS_ACCESS_TOKEN_REFRESH_LOCK, async () => {
          try {
            const keys = getRuntimeAuthKeys()
            const persistedState = JSON.parse(localStorage.getItem(keys.userPersistKey) || '{}')
            const persistedExpiresAt = Number(persistedState.tokenExpiresAt) || 0
            const persistedNotBefore = Number(persistedState.tokenRefreshNotBefore) || 0
            if (
              persistedState.isLoggedIn &&
              persistedState.accessToken &&
              (persistedState.accessToken !== this.accessToken ||
                persistedExpiresAt > (Number(this.tokenExpiresAt) || 0) ||
                persistedNotBefore > (Number(this.tokenRefreshNotBefore) || 0))
            ) {
              this.$patch({
                accessToken: persistedState.accessToken,
                refreshToken: persistedState.refreshToken || this.refreshToken,
                tokenExpiresAt: persistedExpiresAt,
                refreshTokenExpiresAt:
                  persistedState.refreshTokenExpiresAt || this.refreshTokenExpiresAt,
                isLoggedIn: true,
                tokenRefreshNotBefore: persistedNotBefore,
                tokenRefreshNoProgressCount:
                  Number(persistedState.tokenRefreshNoProgressCount) || 0,
              })
              this.syncAuthTokenCookie(this.accessToken)
              this.syncRefreshTokenCookie(this.refreshToken)
              this.syncLoginCookie(true)
            }
          } catch (_) {
            // localStorage 不可用或数据损坏时，退化为当前页签刷新。
          }

          const refreshedByAnotherTab =
            this.accessToken !== accessTokenBeforeLock ||
            (Number(this.tokenExpiresAt) || 0) > expiresAtBeforeLock

          if (
            this.accessToken &&
            !this.shouldRefreshAccessToken &&
            (!force || refreshedByAnotherTab)
          ) {
            this.startTokenRefreshTimer()
            return {
              resp_code: 0,
              access_token: this.accessToken,
              reused: true,
            }
          }

          return this.refreshAccessToken({ force, skipCrossTabLock: true })
        })
      }

      // 创建新的刷新 Promise
      this.refreshPromise = (async () => {
        try {
          this.restoreImsTokenFromCookies()

          // 检查 refresh_token 是否过期
          if (this.isRefreshTokenExpired) {
            this.logout(true)
            throw new Error('refresh_token 已过期')
          }

          if (!this.refreshToken) {
            throw new Error('refresh_token 不存在')
          }

          const previousAccessToken = this.accessToken
          const previousExpiresAt = Number(this.tokenExpiresAt) || 0

          // 动态导入避免循环依赖
          const { imsApiService } = await import('~/api/ims')
          const result = await imsApiService.refreshAccessToken()

          if (result && result.resp_code === 0) {
            // 刷新成功，更新 token
            const newAccessToken = result.access_token || result.accessToken
            if (!newAccessToken) {
              throw new Error('刷新 access_token 失败：返回数据格式错误')
            }

            // 计算过期时间
            const createTime = result.create_time ?? result.createTime
            const expiresIn = result.expires_in ?? result.expiresIn
            const now = Date.now()
            const newTokenExpiresAt = resolveAccessTokenExpiresAt({
              createTime,
              expiresInHours: expiresIn,
              now,
            })
            const hasProgressed = hasTokenRefreshProgressed({
              previousToken: previousAccessToken,
              nextToken: newAccessToken,
              previousExpiresAt,
              nextExpiresAt: newTokenExpiresAt,
              now,
            })
            const noProgressCount = hasProgressed ? 0 : this.tokenRefreshNoProgressCount + 1

            if (noProgressCount > 1) {
              await this.logout(true)
              throw new Error('access_token 刷新结果未推进，已停止重试')
            }

            const tokenRefreshNotBefore = hasProgressed
              ? 0
              : resolveNoProgressRetryAt({ expiresAt: newTokenExpiresAt, now })

            this.$patch({
              accessToken: newAccessToken,
              tokenExpiresAt: newTokenExpiresAt,
              isLoggedIn: true,
              tokenRefreshNotBefore,
              tokenRefreshNoProgressCount: noProgressCount,
            })
            this.syncAuthTokenCookie(newAccessToken)
            this.syncLoginCookie(true)

            // 如果返回数据中包含 cloud_code，保存到 userInfo
            if (result.cloud_code || result.data?.cloud_code) {
              const cloudCode = result.cloud_code || result.data?.cloud_code
              this.setUserInfo({ cloud_code: cloudCode })
            }

            // 刷新结果未推进时，仅在当前 token 实际过期后补偿一次。
            this.startTokenRefreshTimer({ notBefore: tokenRefreshNotBefore })

            return result
          } else {
            this.logout(true)
            throw new Error(`刷新 access_token 失败，错误码: ${result?.resp_code}`)
          }
        } finally {
          // 请求结束，重置 Promise
          this.refreshPromise = null
        }
      })()

      return this.refreshPromise
    },
    /**
     * @description 获取商城用户信息
     * @description 包含请求锁定与短缓存复用逻辑，防止初始化阶段多个组件重复请求
     * @param {{ force?: boolean } | boolean} [options] - force=true 时跳过短缓存
     * @returns {Promise<Object>} 用户信息
     */
    async fetchUserInfo(options = {}) {
      const { force = false } = resolveFetchOptions(options)

      // 如果正在请求中，返回现有的 Promise
      if (this.userInfoPromise) {
        return this.userInfoPromise
      }

      if (!force && this.userInfo && isRecentFetch(this.userInfoFetchedAt, USER_INFO_CACHE_TTL)) {
        return this.userInfo
      }

      // 守卫：/store/user/info 是后端 @Authenticated 端点，必须携带 opentoken。
      // isLoggedIn 仅由 userInfo 推导（见 setUserInfo），无 token 时也可能为 true，
      // 裸发请求必得 401 "Missing opentoken header."。这里按拦截器同源口径判定：
      // 仅当既无可用 token（store accessToken / ims-access-token cookie）、也无可
      // 刷新的 refresh_token 时才跳过；其余交由拦截器带 token / 自动刷新后照常请求。
      this.restoreImsTokenFromCookies()
      const keys = getRuntimeAuthKeys()
      const hasToken =
        !!this.accessToken || !!useCookie(keys.accessTokenCookie, { readonly: true }).value
      const canRefresh = !!this.refreshToken && !this.isRefreshTokenExpired
      if (!hasToken && !canRefresh) {
        return null
      }

      // 创建新的请求 Promise
      this.userInfoPromise = (async () => {
        try {
          const { authApi } = await import('~/api/auth')
          const response = await authApi.getUserInfo()

          if (response && typeof response === 'object') {
            const shouldReuseImsProfile =
              this.hasSyncedImsProfileInfo || hasImsProfileInfo(this.userInfo)
            const nextUserInfo = shouldReuseImsProfile
              ? omitAccountProfileDisplayFields(response)
              : response
            this.manufacturerQuotaLoaded = false
            this.setUserInfo({
              ...nextUserInfo,
              // info 已不含实时额度：先清零，避免持久化旧值残留；异步接口成功后再回填
              manufacturerQuotaRemain: 0,
              manufacturerQuotaTotal: 0,
              cashAvailableBalance: 0,
              kyed: 0,
              klimk: 0,
              manufacturerQuotaOverdue: false,
            })
            this.userInfoFetchedAt = Date.now()
            // 额度已从 info 解耦：不阻塞主 Promise，失败静默（个人中心先显示 0/空）
            void this.fetchManufacturerQuota()
            return response
          } else {
            console.error('[UserStore] 用户信息接口返回数据格式异常:', response)
            throw new Error('数据格式异常')
          }
        } catch (error) {
          console.error('[UserStore] 获取商城用户信息失败:', error)
          // 401 等错误由拦截器处理，这里只需抛出
          throw error
        } finally {
          // 请求结束，重置 Promise
          this.userInfoPromise = null
        }
      })()

      return this.userInfoPromise
    },
    /**
     * @description 异步拉取厂家授信额度并合并到 userInfo（不打断身份/订单计数）
     * @returns {Promise<Object|null>} 额度对象；失败返回 null
     */
    async fetchManufacturerQuota() {
      if (this.manufacturerQuotaPromise) {
        return this.manufacturerQuotaPromise
      }

      const requestVersion = ++this.manufacturerQuotaRequestVersion
      const requestUserId = this.userInfo?.userId ?? this.userInfo?.id ?? null
      const isCurrentRequest = () => {
        const currentUserId = this.userInfo?.userId ?? this.userInfo?.id ?? null
        return (
          this.manufacturerQuotaRequestVersion === requestVersion &&
          this.isLoggedIn &&
          currentUserId === requestUserId
        )
      }

      this.manufacturerQuotaLoaded = false
      if (this.userInfo) {
        this.setUserInfo(normalizeAccountFundsPayload(null))
      }

      const requestPromise = (async () => {
        try {
          const { authApi } = await import('~/api/auth')
          const quota = await authApi.getManufacturerQuota()
          if (!isCurrentRequest() || !quota || typeof quota !== 'object') {
            return null
          }
          this.setUserInfo(normalizeAccountFundsPayload(quota))
          return quota
        } catch (error) {
          console.warn('[UserStore] 获取厂家授信额度失败（已静默）:', error)
          return null
        } finally {
          if (this.manufacturerQuotaRequestVersion === requestVersion) {
            this.manufacturerQuotaLoaded = true
            if (this.manufacturerQuotaPromise === requestPromise) {
              this.manufacturerQuotaPromise = null
            }
          }
        }
      })()

      this.manufacturerQuotaPromise = requestPromise
      return requestPromise
    },
    /**
     * @description 获取成都用户信息（当前登录用户）
     * @description 使用 PUT /app/open/me/info 接口获取当前登录用户的信息
     * @description 成都接口返回字段：account_name（电话号码）、create_time、head_icon（头像）、cloud_code、nick_name（昵称）、source（0：工程宝，1：掌中宝）
     * @param {{ force?: boolean } | boolean} [options] - force=true 时跳过短缓存
     * @returns {Promise<Object>} 用户信息对象
     */
    async fetchImsUserInfo(options = {}) {
      const { force = false } = resolveFetchOptions(options)

      if (this.imsUserInfoPromise) {
        return this.imsUserInfoPromise
      }

      if (
        !force &&
        this.userInfo &&
        isRecentFetch(this.imsUserInfoFetchedAt, IMS_USER_INFO_CACHE_TTL)
      ) {
        return this.userInfo
      }

      this.imsUserInfoPromise = (async () => {
        try {
          // 动态导入避免循环依赖
          const { imsApiService } = await import('~/api/ims')
          const result = await imsApiService.getUserInfo()

          // 成都接口返回有调整，有些环境返回 flat 对象，有些返回 { resp_code, data: { ... } }
          if (result && result.resp_code === 0) {
            // 兼容性处理：优先取 data 字段，如果没有则取顶层字段
            const imsUserInfo = result.data || result

            // 准备需要合并的成都字段
            const imsFields = {
              cloud_code: imsUserInfo.cloud_code,
              nick_name: imsUserInfo.nick_name,
              nick: imsUserInfo.nick_name, // 兼容字段
              nickname: imsUserInfo.nick_name, // 兼容字段
              head_icon: imsUserInfo.head_icon,
              icon: imsUserInfo.head_icon, // 兼容字段
              avatar: imsUserInfo.head_icon, // 兼容字段
              account_name: imsUserInfo.account_name, // 电话号码
              create_time: imsUserInfo.create_time,
              source: imsUserInfo.source, // 0：工程宝，1：掌中宝
              phone: imsUserInfo.account_name, // 兼容字段
            }

            // 过滤逻辑：
            // 1. 过滤掉值为 undefined 或 null 的字段
            // 2. 对于“兼容字段”，如果原有 store 中已有值，则不覆盖，以深圳接口为准
            // @reason 深圳接口包含 userType 等重要信息，且其返回的头像/昵称可能更符合商城系统
            const cleanImsFields = {}
            Object.entries(imsFields).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                // 定义哪些是兼容性/冗余字段
                const isCompatField = ['nick', 'nickname', 'icon', 'avatar', 'phone'].includes(key)

                // 如果 store 中已有该字段值且是兼容字段，则不覆盖，优先保留深圳接口的数据
                if (this.userInfo && this.userInfo[key] && isCompatField) {
                  return
                }

                cleanImsFields[key] = value
              }
            })

            // 使用 setUserInfo 进行合并，确保触发持久化和 Cookie 同步
            this.setUserInfo(cleanImsFields)
            this.imsUserInfoFetchedAt = Date.now()
            this.hasSyncedImsProfileInfo = true

            return imsUserInfo
          } else {
            throw new Error(`获取用户信息失败，错误码: ${result?.resp_code}`)
          }
        } catch (error) {
          console.error('[UserStore] 获取成都用户信息失败:', error)
          throw error
        } finally {
          this.imsUserInfoPromise = null
        }
      })()

      return this.imsUserInfoPromise
    },

    /**
     * @description 退出登录
     * @description 调用成都退出接口，然后清除本地状态
     * @param {boolean} skipApiCall - 是否跳过 API 调用（默认 false）
     * @returns {Promise<void>}
     */
    async logout(skipApiCall = false) {
      // 立即作废资金请求，避免退出接口等待期间或切换账号后被旧响应回写。
      this.manufacturerQuotaRequestVersion += 1
      this.manufacturerQuotaPromise = null
      this.manufacturerQuotaLoaded = false

      // 先调用退出接口（如果不需要跳过）
      if (!skipApiCall) {
        try {
          const { imsApiService } = await import('~/api/ims')
          await imsApiService.logout().catch((error) => {
            // 退出接口失败不影响本地状态清除
          })
        } catch (error) {
          // 如果导入失败或其他错误，不影响退出流程
        }
      }

      // 清除本地状态
      // 先清 token（会停止刷新定时器）
      this.clearImsToken()
      // 用 $patch 统一更新，确保持久化插件订阅到变更
      this.$patch({
        userInfo: null,
        isLoggedIn: false,
        accessToken: null,
        refreshToken: null,
        tokenExpiresAt: null,
        refreshTokenExpiresAt: null,
        userInfoFetchedAt: 0,
        imsUserInfoFetchedAt: 0,
        hasSyncedImsProfileInfo: false,
      })
      this.syncLoginCookie(false)
      if (import.meta.client) {
        try {
          const nuxtApp = useNuxtApp()
          if (nuxtApp.$setMonitorUser) nuxtApp.$setMonitorUser(null, null)
        } catch (_) {}
      }

      // 清理商城相关的其他 Store 状态
      try {
        // 使用动态导入避免 Store 之间的循环依赖
        const [
          { useStoreCartStore },
          { useAddressStore },
          { useStoreCompareStore },
          { useStorePayStore },
          { useStoreProductStore },
        ] = await Promise.all([
          import('~/store/store/cart'),
          import('~/store/store/address'),
          import('~/store/store/compare'),
          import('~/store/store/pay'),
          import('~/store/store/product'),
        ])

        const cartStore = useStoreCartStore()
        const addressStore = useAddressStore()
        const compareStore = useStoreCompareStore()
        const payStore = useStorePayStore()
        const productStore = useStoreProductStore()

        cartStore.clearCartState()
        addressStore.clearAddressState()
        compareStore.clearCompareState()
        payStore.clearPayState()
        productStore.clearProductState()
      } catch (error) {
        console.error('[UserStore] 清理商城相关 Store 状态失败:', error)
      }

      // 兜底：强制清理 localStorage（避免此前手动写入或持久化插件异常导致残留）
      if (import.meta.client && typeof localStorage !== 'undefined') {
        try {
          const keys = getRuntimeAuthKeys()
          localStorage.removeItem(keys.userPersistKey)
        } catch (e) {
          // ignore
        }
      }
    },

    /**
     * @description 开发环境初始化 Session
     * @description 用于 3-15 前所有环境写死 Token 并初始化登录态
     */
    async initDevSession() {
      if (!IMS_DEV_MODE) return
      if (new Date().getTime() >= 1773619200000) return // 2026-03-15 之后失效

      const hardcodedAccessToken =
        'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ6aGFuZ2ppa2FpZmEiLCJhdWQiOiJ1cm46c2hvcDpzZXJ2aWNlIiwiYWNjZXNzX3R5cGUiOiJhZG1pbiIsIm5iZiI6MTc2NDkyNzQ3OSwiY2xvdWRfY29kZSI6InpoYW5namlrYWlmYSIsImlzcyI6InVybjppbXM6YXV0aCIsInNvdXJjZSI6MSwidG9rZW5fdHlwZSI6ImFjY2VzcyIsImV4cCI6MTgwMDkyNzQ3OSwiaWF0IjoxNzY0OTI3NDc5LCJqdGkiOiJmNGE3OTU2Ni1mZjU3LTRlNTAtYThlNS0yZGMwZTc1NmJjNTEifQ.hzmComQczLQbo1s7WAuCSO3wotszDgXCV8xfRHfuruA'
      const hardcodedRefreshToken =
        'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ6aGFuZ2ppa2FpZmEiLCJhdWQiOiJ1cm46c2hvcDpzZXJ2aWNlIiwiYWNjZXNzX3R5cGUiOiJhZG1pbiIsIm5iZiI6MTc2NDkyNzQ3OSwiY2xvdWRfY29kZSI6InpoYW5namlrYWlmYSIsImlzcyI6InVybjppbXM6YXV0aCIsInNvdXJjZSI6MSwidG9rZW5fdHlwZSI6ImFjY2VzcyIsImV4cCI6MTgwMDkyNzQ3OSwiaWF0IjoxNzY0OTI3NDc5LCJqdGkiOiJmNGE3OTU2Ni1mZjU3LTRlNTAtYThlNS0yZGMwZTc1NmJjNTEifQ.hzmComQczLQbo1s7WAuCSO3wotszDgXCV8xfRHfuruA'

      this.$patch({
        accessToken: hardcodedAccessToken,
        refreshToken: hardcodedRefreshToken,
        isLoggedIn: true,
        // 设置较长的过期时间，避免开发模式下频繁刷新
        tokenExpiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
        refreshTokenExpiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
      })

      if (import.meta.client) {
        this.syncAuthTokenCookie(hardcodedAccessToken)
        this.syncRefreshTokenCookie(hardcodedRefreshToken)
        this.syncLoginCookie(true)

        // 初始化用户信息
        if (!this.userInfo) {
          try {
            await this.fetchImsUserInfo()
          } catch (e) {
            console.error('[DevSession] 自动获取用户信息失败:', e)
          }
        }
      }
    },
  },
})
